import type { AuthModel, RecordModel, SendOptions, RecordListOptions, FileOptions } from 'pocketbase';

export const useDbStore = defineStore('db', () => {
  const { $pb } = useNuxtApp();
  // console.log($pb);
  const authRefresh = async () => {
    return $pb.collection('users').authRefresh({ expand: 'orders' });
  };

  const createUser = async (email: string, password: string, passwordConfirm: string, name: string) => {
    return $pb.collection('users').create({
      name,
      email,
      password,
      passwordConfirm,
    });
  };

  const loginByEmail = async (email: string, password: string) => {
    return $pb.collection('users').authWithPassword(email, password, { expand: 'orders' });
  };

  const loginByGoogle = async () => {
    const options: {
      provider: 'google';
      urlCallback?: (url: string) => void;
      expand: string;
    } = {
      provider: 'google',
      expand: 'orders',
    };

    const { isMobile } = useDevice();

    const w = isMobile ? (window.open() as Window | null) : null;

    if (w) {
      options.urlCallback = (url) => {
        w.location.href = url;
      };
    }

    const authResponse = $pb.collection('users').authWithOAuth2(options);
    // console.log('loginByGoogle', authResponse);
    return authResponse;
  };

  const loginByApple = async () => {
    // TODO: implement login
  };

  const logout = async () => {
    console.info('logged out');
    // this.unsubscribeFromAllCollections();
    $pb.collection('users').unsubscribe();
    // user.value = null;
    return $pb.authStore.clear();
  };

  const onValidationChange = async (cb: (record: AuthModel) => void) => {
    $pb.authStore.onChange((token, model) => {
      // console.log('onValidationChange MODEL', model);
      cb?.(model);
    });
  };

  const subscribeToCollection = (
    collectionName: 'users' | 'orders',
    cb: (data: any) => void,
    id = '*',
    options?: SendOptions,
  ) => {
    // console.log('subscribe', collectionName);
    $pb.collection(collectionName).subscribe(id, cb, options);
  };

  const unsubscribeFromCollection = (collectionName: 'users' | 'orders', id = '') => {
    $pb.collection(collectionName).unsubscribe(id);
  };

  const unsubscribeFromAllCollections = () => {
    $pb.collection('users').unsubscribe();
    $pb.collection('orders').unsubscribe();
  };

  const createOrder = async (order: ServerOrderI) => {
    // console.log('CREATED ORDER ON SERVER', order);
    return $pb.collection('orders').create(order);
  };

  const updateOrder = async (id: string, orderPartial: Partial<ServerOrderI>) => {
    return $pb.collection('orders').update(id, orderPartial);
  };

  const addOrderFiles = async (id: string, files: File[]) => {
    return $pb.collection('orders').update(id, {
      files: files,
    });
  };

  const removeOrderFile = async (id: string, fileName: string) => {
    return $pb.collection('orders').update(id, {
      'files-': [fileName],
    });
  };

  const deleteOrder = async (id: string) => {
    return $pb.collection('orders').delete(id);
  };

  const getOrders = async (options: RecordListOptions) => {
    return $pb.collection('orders').getFullList({ expand: 'user', ...options });
  };

  const getOrder = async (id: string) => {
    return $pb.collection('orders').getOne(id);
  };

  const resetPassword = async (email: string) => {
    return $pb.collection('users').requestPasswordReset(email);
  };

  const confirmResetPassword = async (token: string, password: string, confirmPassword: string) => {
    return $pb.collection('users').confirmPasswordReset(token, password, confirmPassword);
  };

  const requestVerification = async (email: string) => {
    return $pb.collection('users').requestPasswordReset(email);
  };

  const confirmVerification = async (token: string) => {
    return $pb.collection('users').requestPasswordReset(token);
  };

  const updateUserData = async (data: Record<string, any>) => {
    // console.log('updateUserData', data);

    if (!$pb.authStore.model) {
      console.warn('unauthorized');
      return;
    };
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
    return $pb.collection('users').update($pb.authStore.model.id, data);
  };

  const getCartPrice = async () => {
    const userModel = await $pb.collection('users').getOne<UserI>($pb.authStore.model?.id, { expand: 'cart,cart.orders' });
    if (!userModel) return { totalPrice: '0.00', totalPriceAfterDiscount: '0.00', discount: '0.00' };

    const loyaltyDiscount = userModel.loyaltyDiscount || 0;

    const orders = userModel?.expand?.cart?.expand?.orders || [];
    const totalPrice: number = orders.reduce((acc, order) => {
      return acc + calculateOrderPrice({
        stickers: order.customizer.stickers,
        item: getItemByTypeAndName(order.itemType, order.itemName),
      });
    }, 0);

    const totalPriceAfterDiscount: number = orders.reduce((acc, order) => {
      return acc + calculateOrderPrice({
        stickers: order.customizer.stickers,
        item: getItemByTypeAndName(order.itemType, order.itemName),
      }, loyaltyDiscount);
    }, 0);

    const discount = totalPrice - totalPriceAfterDiscount;

    return { totalPrice: totalPrice.toFixed(2), totalPriceAfterDiscount: totalPriceAfterDiscount.toFixed(2), discount: discount.toFixed(2) };
  };

  const createPayment = async (paymentDetails: any) => {
    if (!$pb.authStore.model?.id) return;

    return await $pb.collection('payments').create({
      details: paymentDetails,
      user: $pb.authStore.model!.id,
      // orders: orderIds,
    });
  };

  return {
    updateUserData,

    authRefresh,
    createUser,
    loginByEmail,
    loginByGoogle,
    loginByApple,
    logout,

    resetPassword,
    confirmResetPassword,
    requestVerification,
    confirmVerification,

    onValidationChange,

    createOrder,
    updateOrder,
    addOrderFiles,
    removeOrderFile,
    deleteOrder,
    getOrders,
    getOrder,

    subscribeToCollection,
    unsubscribeFromCollection,
    unsubscribeFromAllCollections,

    getCartPrice,
    createPayment,

    auth: $pb.authStore,
  };
});
